import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { RefreshCw, Search, Package, ExternalLink, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = "/api";

export default function ShopifyProductsPage() {
  const [token] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, archived: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadLimit, setLoadLimit] = useState(100); // Varsayılan 100 ürün

  // Shopify ürünlerini getir
  const fetchShopifyProducts = async (limit = loadLimit) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shopify/all-products?token=${token}&limit=${limit}`);
      if (response.data.success) {
        const prods = response.data.products || [];
        setProducts(prods);
        setFilteredProducts(prods);
        
        // İstatistikleri hesapla
        const active = prods.filter(p => p.status === "active").length;
        const draft = prods.filter(p => p.status === "draft").length;
        const archived = prods.filter(p => p.status === "archived").length;
        setStats({ total: prods.length, active, draft, archived });
        
        toast.success(`${prods.length} ürün yüklendi`);
      } else {
        toast.error(response.data.error || "Ürünler yüklenemedi");
      }
    } catch (error) {
      console.error("Shopify products error:", error);
      toast.error(error.response?.data?.detail || "Ürünler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Tüm ürünleri getir (arka planda)
  const fetchAllProducts = async () => {
    setLoading(true);
    toast.info("Tüm ürünler yükleniyor, bu biraz zaman alabilir...");
    try {
      const response = await axios.get(`${API}/shopify/all-products?token=${token}&limit=0`);
      if (response.data.success) {
        const prods = response.data.products || [];
        setProducts(prods);
        setFilteredProducts(prods);
        
        const active = prods.filter(p => p.status === "active").length;
        const draft = prods.filter(p => p.status === "draft").length;
        const archived = prods.filter(p => p.status === "archived").length;
        setStats({ total: prods.length, active, draft, archived });
        
        toast.success(`Tüm ürünler yüklendi: ${prods.length} ürün`);
      } else {
        toast.error(response.data.error || "Ürünler yüklenemedi");
      }
    } catch (error) {
      console.error("Shopify all products error:", error);
      toast.error("Tüm ürünler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Arama ve filtreleme
  useEffect(() => {
    let filtered = products;
    
    // Durum filtresi
    if (statusFilter !== "all") {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    // Arama
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(term) ||
        p.product_type?.toLowerCase().includes(term) ||
        p.vendor?.toLowerCase().includes(term) ||
        p.variants?.some(v => 
          v.sku?.toLowerCase().includes(term) ||
          v.barcode?.toLowerCase().includes(term)
        )
      );
    }
    
    setFilteredProducts(filtered);
  }, [searchTerm, statusFilter, products]);

  // Ürün seçimi toggle
  const toggleProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Ürün detaylarını aç/kapa
  const toggleExpand = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // Toplam stok hesapla
  const getTotalStock = (variants) => {
    return variants?.reduce((sum, v) => sum + (v.inventory_quantity || 0), 0) || 0;
  };

  // Fiyat aralığı hesapla
  const getPriceRange = (variants) => {
    if (!variants || variants.length === 0) return "N/A";
    const prices = variants.map(v => parseFloat(v.price) || 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return `${min.toLocaleString('tr-TR')} ₺`;
    return `${min.toLocaleString('tr-TR')} - ${max.toLocaleString('tr-TR')} ₺`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="shopify-products-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shopify Ürünleri</h1>
          <p className="text-gray-500 mt-1">Shopify mağazanızdaki ürünleri görüntüleyin</p>
        </div>
        <div className="flex items-center gap-2">
          <select 
            value={loadLimit} 
            onChange={(e) => setLoadLimit(Number(e.target.value))}
            className="border rounded-md px-3 py-2 text-sm"
          >
            <option value={50}>İlk 50</option>
            <option value={100}>İlk 100</option>
            <option value={250}>İlk 250</option>
            <option value={0}>Tümü</option>
          </select>
          <Button onClick={() => fetchShopifyProducts(loadLimit)} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Yükleniyor..." : "Ürünleri Getir"}
          </Button>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-blue-500" onClick={() => setStatusFilter("all")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-green-500 ${statusFilter === "active" ? "border-green-500" : ""}`} onClick={() => setStatusFilter("active")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aktif</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <Badge className="bg-green-100 text-green-800">Active</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-yellow-500 ${statusFilter === "draft" ? "border-yellow-500" : ""}`} onClick={() => setStatusFilter("draft")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Taslak</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.draft}</p>
              </div>
              <Badge className="bg-yellow-100 text-yellow-800">Draft</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-gray-500 ${statusFilter === "archived" ? "border-gray-500" : ""}`} onClick={() => setStatusFilter("archived")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Arşivlenmiş</p>
                <p className="text-2xl font-bold text-gray-600">{stats.archived}</p>
              </div>
              <Badge className="bg-gray-100 text-gray-800">Archived</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Arama ve Filtreler */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Ürün adı, SKU veya barkod ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" onClick={() => { setSearchTerm(""); setStatusFilter("all"); }}>
              <Filter className="w-4 h-4 mr-2" />
              Filtreleri Temizle
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Ürün Listesi */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Ürünler ({filteredProducts.length})
            </CardTitle>
            {filteredProducts.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-gray-500">
                  {selectedProducts.size > 0 ? `${selectedProducts.size} seçili` : "Tümünü seç"}
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredProducts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {products.length === 0 
                ? "Henüz ürün yüklenmedi. 'Ürünleri Getir' butonuna tıklayın."
                : "Arama kriterinize uygun ürün bulunamadı."
              }
            </div>
          ) : (
            <div className="divide-y">
              {filteredProducts.map((product) => (
                <div key={product.id} className="hover:bg-gray-50">
                  {/* Ürün Satırı */}
                  <div className="p-4 flex items-center gap-4">
                    <Checkbox 
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => toggleProduct(product.id)}
                    />
                    
                    {/* Ürün Resmi */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {product.image?.src ? (
                        <img 
                          src={product.image.src} 
                          alt={product.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Ürün Bilgileri */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{product.title}</h3>
                        <Badge 
                          className={
                            product.status === "active" ? "bg-green-100 text-green-800" :
                            product.status === "draft" ? "bg-yellow-100 text-yellow-800" :
                            "bg-gray-100 text-gray-800"
                          }
                        >
                          {product.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span>{product.product_type || "Tip yok"}</span>
                        <span>•</span>
                        <span>{product.vendor || "Marka yok"}</span>
                        <span>•</span>
                        <span>{product.variants?.length || 0} varyant</span>
                      </div>
                    </div>
                    
                    {/* Stok ve Fiyat */}
                    <div className="text-right flex-shrink-0">
                      <div className="font-medium text-gray-900">{getPriceRange(product.variants)}</div>
                      <div className={`text-sm ${getTotalStock(product.variants) > 0 ? "text-green-600" : "text-red-600"}`}>
                        Stok: {getTotalStock(product.variants)}
                      </div>
                    </div>
                    
                    {/* Aksiyonlar */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleExpand(product.id)}
                      >
                        {expandedProducts.has(product.id) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                      <a 
                        href={`https://${product.store_url || "shop"}/admin/products/${product.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                  
                  {/* Varyant Detayları (Expanded) */}
                  {expandedProducts.has(product.id) && (
                    <div className="bg-gray-50 px-4 pb-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="py-2 font-medium">Varyant</th>
                            <th className="py-2 font-medium">SKU</th>
                            <th className="py-2 font-medium">Barkod</th>
                            <th className="py-2 font-medium text-right">Fiyat</th>
                            <th className="py-2 font-medium text-right">Stok</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {product.variants?.map((variant) => (
                            <tr key={variant.id}>
                              <td className="py-2">{variant.title}</td>
                              <td className="py-2 font-mono text-xs">{variant.sku || "-"}</td>
                              <td className="py-2 font-mono text-xs">{variant.barcode || "-"}</td>
                              <td className="py-2 text-right">{parseFloat(variant.price).toLocaleString('tr-TR')} ₺</td>
                              <td className={`py-2 text-right font-medium ${(variant.inventory_quantity || 0) > 0 ? "text-green-600" : "text-red-600"}`}>
                                {variant.inventory_quantity || 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
